import { getCollisionStateConfig, isKnownCollisionState } from '@/components/collision/collisionStateConfig';
import { MOTION } from '@/components/common/motion';
import { CARD_PADDED, SECTION_LABEL } from '@/components/common/surfaces';
import type { ProjectModel } from '@/types/project.types';
import { ArrowSquareOut, ShieldSlash } from '@phosphor-icons/react';
import { Tooltip } from 'primereact/tooltip';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

type CollisionProps = Pick<ProjectModel, 'collision_project_id' | 'collision_simulation_state' | 'collision_url'>;

interface Props {
  project: CollisionProps;
}

/** The tint a state paints itself in: the tile behind the icon, the dot, and the state's words. */
interface StateTint {
  tile: string;
  dot: string;
  text: string;
}

/**
 * `ICON_TILE`'s geometry without its fixed gray, because the tile here is tinted per state.
 *
 * Appending a background to `ICON_TILE` would put two `bg-*` utilities on one element, and which
 * of the two wins comes down to the order Tailwind emitted them — not to the order written here.
 */
const STATE_TILE = 'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg';

/**
 * Semantic colours per state, owned here rather than taken from `collisionStateConfig`.
 *
 * The shared config still supplies the icon — it is what the compact list tag and this banner
 * have to agree on. The tints are decided here because this banner needs a lighter tile than the
 * compact tag does, but the *choice of hue* matches `collisionStateConfig` state for state: only
 * the three states that carry a verdict spend a colour. "Running" is the absence of a verdict, so
 * it stays neutral and lets the spinning icon and the pulsing dot say it is working — which is
 * also what keeps the single `primary` accent meaningful.
 */
const STATE_TINTS: Record<string, StateTint> = {
  running: { tile: 'bg-gray-100 text-gray-500', dot: 'bg-gray-400 animate-pulse', text: 'text-gray-700' },
  // biome-ignore lint/style/useNamingConvention: keys mirror backend collision_simulation_state values
  complete_ok: { tile: 'bg-emerald-50 text-emerald-600', dot: 'bg-emerald-500', text: 'text-emerald-700' },
  // biome-ignore lint/style/useNamingConvention: keys mirror backend collision_simulation_state values
  complete_collisions: { tile: 'bg-amber-50 text-amber-600', dot: 'bg-amber-500', text: 'text-amber-700' },
  error: { tile: 'bg-red-50 text-red-600', dot: 'bg-red-500', text: 'text-red-700' },
};

/** Unknown state, and the banner before any simulation has run. */
const FALLBACK_TINT: StateTint = { tile: 'bg-gray-100 text-gray-400', dot: 'bg-gray-300', text: 'text-gray-500' };

/**
 * The link out to the Collisions app.
 *
 * An anchor rather than an `ActionButton`, because it navigates — a `button` nested in an `a` is
 * not valid content — so it borrows the secondary button's recipe instead of being one.
 */
const LINK_BUTTON_CLASSES = `inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:border-gray-300 hover:bg-gray-50 active:scale-[0.98] ${MOTION} focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2`;

/**
 * The collision simulation's state, as one line above the panels.
 *
 * Deliberately a single row rather than a panel of its own: it reports a status, it holds no
 * content, and giving it a heading would put it on the same footing as the NC document below it.
 */
export const CollisionDetectionPanel: FC<Props> = ({ project }) => {
  const { t } = useTranslation();

  const hasCollisionData = !!(project.collision_project_id ?? project.collision_simulation_state);
  const state = project.collision_simulation_state;
  const canOpen = !!(project.collision_url && state && state !== 'running');

  if (!hasCollisionData) {
    return (
      <div className={`${CARD_PADDED} flex items-center gap-4`}>
        <span className={`${STATE_TILE} ${FALLBACK_TINT.tile}`}>
          <ShieldSlash size={16} aria-hidden="true" />
        </span>
        <div className="flex min-w-0 flex-col gap-1">
          <span className={SECTION_LABEL}>{t('projectDetail.collisionDetection')}</span>
          <Tooltip target=".collision-unavailable-hint" position="right" />
          <span className="flex items-center gap-2">
            <span className={`h-2 w-2 shrink-0 rounded-full ${FALLBACK_TINT.dot}`} />
            <span
              className={`collision-unavailable-hint cursor-help text-sm font-medium ${FALLBACK_TINT.text}`}
              data-pr-tooltip={t(
                'projectDetail.collisionUnavailableTooltip',
                'Collision detection requires both a target model and a blank model to be uploaded.'
              )}
            >
              {t('projectDetail.collisionUnavailable')}
            </span>
          </span>
        </div>
      </div>
    );
  }

  const cfg = getCollisionStateConfig(state);
  const Icon = cfg.icon;
  const tint = (state && STATE_TINTS[state]) || FALLBACK_TINT;
  const label = isKnownCollisionState(state) ? t(`projectDetail.collisionStates.${state}`, state) : (state ?? '—');

  return (
    <div className={`${CARD_PADDED} flex flex-wrap items-center justify-between gap-4`}>
      <div className="flex min-w-0 items-center gap-4">
        <span className={`${STATE_TILE} ${tint.tile}`}>
          <Icon className={`h-4 w-4 ${state === 'running' ? 'animate-spin motion-reduce:animate-none' : ''}`} />
        </span>
        <div className="flex min-w-0 flex-col gap-1">
          <span className={SECTION_LABEL}>{t('projectDetail.collisionDetection')}</span>
          <span className="flex items-center gap-2">
            <span className={`h-2 w-2 shrink-0 rounded-full ${tint.dot}`} />
            <span className={`text-sm font-medium ${tint.text}`}>{label}</span>
          </span>
        </div>
      </div>

      {canOpen && (
        <a href={project.collision_url!} target="_blank" rel="noopener noreferrer" className={LINK_BUTTON_CLASSES}>
          <ArrowSquareOut size={16} aria-hidden="true" />
          {t('projectDetail.openInCollisions')}
        </a>
      )}
    </div>
  );
};
